/**
 * Q6 playback HUD — a small floating overlay anchored to the canvas
 * corner showing tick progress and (optional) speed picker.
 *
 * Uses drei's `<Html>` so the widget composes inside the Canvas tree
 * without polluting the wrapping page DOM. The consumer decides
 * whether to enable the speed picker by passing `onSpeedChange`; if
 * absent, the widget shows speed read-only.
 */

import { Html } from "@react-three/drei";

export interface PlaybackHudProps {
  currentTick: number;
  maxTick: number;
  /** Active playback speed multiplier. */
  speed?: number;
  /** When provided, render the speed picker UI. */
  onSpeedChange?: (speed: number) => void;
  /** Hide entirely. */
  hidden?: boolean;
}

const SPEED_OPTIONS = [0.5, 1, 2, 4] as const;
const STYLE: React.CSSProperties = {
  position: "absolute",
  top: 16,
  left: 16,
  padding: "8px 12px",
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 12,
  fontWeight: 600,
  color: "#e2e8f0",
  background: "rgba(15, 23, 42, 0.72)",
  border: "1px solid rgba(148, 163, 184, 0.25)",
  borderRadius: 6,
  pointerEvents: "auto",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const PILL_STYLE: React.CSSProperties = {
  border: "1px solid rgba(148, 163, 184, 0.3)",
  borderRadius: 4,
  padding: "2px 8px",
  background: "transparent",
  color: "#cbd5e1",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 600,
};

const PILL_ACTIVE_STYLE: React.CSSProperties = {
  ...PILL_STYLE,
  background: "#38bdf8",
  borderColor: "#38bdf8",
  color: "#0f172a",
};

export function PlaybackHud({
  currentTick,
  maxTick,
  speed,
  onSpeedChange,
  hidden = false,
}: PlaybackHudProps) {
  if (hidden) return null;
  return (
    <Html as="div" calculatePosition={topLeftFixed} prepend>
      <div style={STYLE}>
        <span>
          tick {currentTick.toString().padStart(2, "0")} / {maxTick.toString().padStart(2, "0")}
        </span>
        {onSpeedChange ? (
          <span style={{ display: "flex", gap: 4 }}>
            {SPEED_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onSpeedChange(s)}
                style={speed === s ? PILL_ACTIVE_STYLE : PILL_STYLE}
              >
                {s}×
              </button>
            ))}
          </span>
        ) : speed !== undefined ? (
          <span style={{ opacity: 0.7 }}>{speed}×</span>
        ) : null}
      </div>
    </Html>
  );
}

/** Pin the HUD to the canvas's top-left in screen space. */
function topLeftFixed(): [number, number, number] {
  return [0, 0, 0];
}
